/*
    Copyright 2020 Lauren Gyldenlove.
    This file is part of Duelmaster.

    Duelmaster is free software: you can redistribute it and/or modify
    it under the terms of the GNU General Public License as published by
    the Free Software Foundation, either version 3 of the License, or
    (at your option) any later version.

    Duelmaster is distributed in the hope that it will be useful,
    but WITHOUT ANY WARRANTY; without even the implied warranty of
    MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
    GNU General Public License for more details.

    You should have received a copy of the GNU General Public License
    along with Foobar.  If not, see <https://www.gnu.org/licenses/>.
 */

import React from 'react';
import { mdiCircleMultiple } from '@mdi/js';
import { makeStyles } from '@material-ui/styles';
import { Dialog, DialogTitle, DialogContent, SvgIcon, ListItem, ListItemIcon, ListItemText, FormControl, Grid, FormLabel, RadioGroup, FormControlLabel, Radio, Typography } from '@material-ui/core';
import { Coin } from './Coin';
import { AppSettings } from '../model/AppSettings';
import { AppContext } from '../App';
import * as Player from '../model/Player';


const useStyles = makeStyles({
    coinWrapper: {
        textAlign: "center"
    }
});

export const CoinFlipToolItem = () => {
    const appSettings: AppSettings = React.useContext(AppContext);
    const [open, setOpen] = React.useState(false);
    const [player, setPlayer] = React.useState(Player.Position.LEFT);
    const [tossCallIsHeads, setTossCall] = React.useState(true);
    const [actualCallIsHeads, setActualCall] = React.useState(false);
    const [isCalled, setCalled] = React.useState(false);
    const classes = useStyles();

    const handleOpen = () => {
        setOpen(true);
    }

    const handleClose = () => {
        setOpen(false);
    }

    const handleFlipResult = (isHeads: boolean) => {
        console.log(isHeads);
        setActualCall(isHeads);
        setCalled(true);
    }

    const handleSetPlayer = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPlayer(Number(event.currentTarget.value) || 0 as Player.Position);
    }

    const handleCallToss = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTossCall(event.currentTarget.value === 'heads');
    }

    const getPlayer = () => {
        if(player === Player.Position.LEFT) {
            return appSettings.leftPlayer;
        } else {
            return appSettings.rightPlayer;
        }
    }

    return (
        <React.Fragment>
            <ListItem button onClick={handleOpen}>
                <ListItemIcon>
                    <SvgIcon>
                        <path d={mdiCircleMultiple} />
                    </SvgIcon>
                </ListItemIcon>
                <ListItemText primary="Flip Coin" />
            </ListItem>
            <Dialog 
                aria-labelledby="coin-flip-title"
                aria-describedby="coin-flip-description"
                maxWidth={"md"}
                keepMounted
                open={open}
                onClose={handleClose}>
                    <DialogTitle id="coin-flip-title">Coin Flip</DialogTitle>
                    <DialogContent id="coin-flip-description">
                        <Grid container justify="center" alignItems="center">
                            <Grid item xs={6}>
                                <FormControl>
                                    <FormLabel id="select-player-label">Player</FormLabel>
                                    <RadioGroup aria-label="select-player-label" name="select-player" value={player} onChange={handleSetPlayer}>
                                        <FormControlLabel value={Player.Position.LEFT} label={appSettings.leftPlayer.name} control={<Radio />}/>
                                        <FormControlLabel value={Player.Position.RIGHT} label={appSettings.rightPlayer.name} control={<Radio />} />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6}>
                                <FormControl>
                                    <FormLabel id="coin-toss-call-label">Calls</FormLabel>
                                    <RadioGroup aria-label="coin-toss-call-label" name="coin-toss-call" value={tossCallIsHeads ? 'heads' : 'tails'} onChange={handleCallToss}>
                                        <FormControlLabel value={'heads'} label={'Heads'} control={<Radio />} />
                                        <FormControlLabel value={'tails'} label={'Tails'} control={<Radio />} />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} className={classes.coinWrapper}>
                                <Coin flipSeconds={500} setFlipResult={handleFlipResult} />
                            </Grid>
                            <Grid item xs={12} className={classes.coinWrapper}>
                                <TossCall tossCalled={isCalled} calledTossIsHeads={tossCallIsHeads} actualTossIsHeads={actualCallIsHeads} shotCaller={getPlayer().name} />
                            </Grid>
                        </Grid>
                    </DialogContent>
            </Dialog>
        </React.Fragment>
    );
}

interface TossCallProps {
    tossCalled: boolean;
    calledTossIsHeads: boolean;
    actualTossIsHeads: boolean;
    shotCaller: string;
}

const TossCall = (props: TossCallProps) => {
    if(!props.tossCalled) {
        return (
            <Typography>Tap or click the coin to toss.</Typography>
        );
    }

    if(props.calledTossIsHeads === props.actualTossIsHeads) {
        return (
            <Typography>{props.shotCaller} has won the toss!</Typography>
        );
    } else {
        return (
            <Typography>{props.shotCaller} has lost the toss!</Typography>
        )
    }
}
